import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useQueryCallbacks } from 'core/hooks/data/useQueryCallbacks'
import useCurrentTeam from 'core/hooks/team/useCurrentTeam'
import { useSettings } from 'core/hooks/settings/useSettings'
import useNotifications from 'core/hooks/ui/useNotifications'
import useLocalStorage from 'core/hooks/utils/useLocalStorage'
import APIService from 'api/APIService'
import { downloadFile } from 'utils/AppHelper'
import { ID, JobStatus } from 'types/entity'
import Button from 'core/components/buttons/Button'

interface BulkUpdateProps {
  onSuccess?: VoidFunction
}

const useReportBulkUpdate = ({ onSuccess }: BulkUpdateProps = {}) => {
  const queryClient = useQueryClient()
  const { data: currentTeam } = useCurrentTeam()
  const { data: settings, set } = useSettings()
  const notification = useNotifications()
  const { token_id: teamId } = currentTeam ?? {}
  const [localJobId, setJobId] = useLocalStorage('report_update_job_id', '')
  const jobID = settings.pollingJobs.payPeriodDownloadJobId || localJobId
  const { mutate: downloadCSVTemplate, status: templateStatus } = useMutation({
    mutationFn: () => {
      // eslint-disable-next-line
      return APIService.reports.getReportCSVTemplate({ teamId: teamId! })
    },
    onSuccess: (data) => {
      const csvContent = `data:text/csv;charset=utf-8,${data}`
      const encodedURI = encodeURI(csvContent)
      downloadFile(encodedURI, 'update-report-template')
    },
    onError: () => {
      notification.error('An error has occurred trying to download this file')
    }
  })

  useQueryCallbacks({
    queryKey: ['member-export-polling', jobID],
    queryFn: () => APIService.misc.getJobStatus({ jobId: jobID }),
    enabled: !!jobID,
    refetchInterval: 2000,
    onSuccess: (response: JobStatus) => {
      if (response.status === 'finished' && !response.data?.errors) {
        notification.dismiss('reportBulkUpdateJobId')
        setJobId('')
        set('pollingJobs', {
          ...settings.pollingJobs,
          reportBulkUpdateJobId: ''
        })
        queryClient.invalidateQueries({
          queryKey: ['members', currentTeam?.token_id]
        })
        queryClient.invalidateQueries({ queryKey: ['one-off-reports'] })
        queryClient.invalidateQueries({ queryKey: ['reports'] })
        queryClient.invalidateQueries({ queryKey: ['pay-periods'] })
        notification.success({
          title: response.data.message as string,
          id: jobID
        })
      }

      if (
        response.status === 'failed' ||
        (response.status === 'finished' && response.data?.errors)
      ) {
        notification.dismiss('reportBulkUpdateJobId')
        notification.error({
          title: 'There were some errors with your file',
          subtitle: (
            <div>
              {typeof response.data?.message === 'string' &&
                response.data.message?.split(';').map((error) => {
                  return <span key={error}>{error.replace(';', '')}</span>
                })}
            </div>
          ),
          id: jobID,
          footer: () => {
            return (
              <Button
                className="text-primary"
                variant="link"
                onClick={() => {
                  downloadCSVTemplate()
                }}
              >
                Download CSV template
              </Button>
            )
          }
        })
        setJobId('')
        set('pollingJobs', {
          ...settings.pollingJobs,
          reportBulkUpdateJobId: ''
        })
      }
    },
    onError: () => {
      notification.dismiss('reportBulkUpdateJobId')
      notification.error({
        title: 'Could not update members at this time',
        id: jobID
      })
      setJobId('')
      set('pollingJobs', {
        ...settings.pollingJobs,
        reportBulkUpdateJobId: ''
      })
    }
  })

  const uploadQuery = useMutation({
    mutationKey: ['upload', 'update', 'csv'],
    mutationFn: ({ csv, teamId }: { csv: File; teamId: ID }) => {
      return APIService.reports.uploadCsvBulkReportUpdate({
        file: csv as File,
        teamId
      })
    },
    onSuccess: (data) => {
      notification.success({
        title: 'Upload successful',
        duration: 3000,
        id: 'bulkUpdateUpload'
      })

      notification.loading({
        title: 'Updating members...',
        subtitle:
          'This may take a few minutes. You can close this pop up and we’ll keep working in the background.',
        duration: Infinity,
        id: 'reportBulkUpdateJobId'
      })
      set('pollingJobs', {
        ...settings.pollingJobs,
        reportBulkUpdateJobId: data.job_token_id
      })
      setJobId(data.job_token_id)
      onSuccess?.()
    },
    onError: () => {
      notification.error({
        title: 'An error has occurred trying to upload your file',
        id: 'reportBulkUpdateJobId'
      })
    }
  })

  return {
    downloadCSVTemplate,
    uploadQuery,
    templateStatus
  }
}

export default useReportBulkUpdate
